<template>
  <v-dialog
    max-width="600"
    v-model="quickEditDialog"
    class="template-pop-up template-pop-up-fix">
    <EditAssetModal
      v-on:close="quickEditDialog = false"
      :selected="assetCache"
      v-if="quickEditDialog" />
  </v-dialog>
  <div
    ref="container"
    style="
      display: flex;
      flex-direction: column;
      width: 100%;
      margin-bottom: 20px;
    ">
    <div style="display: flex; flex-direction: column; width: 100%">
      <div style="display: flex; position: relative">
        <v-textarea
          ref="textArea"
          autofocus
          dense
          :disabled="realtime_mode || readOnlyMode"
          clearable
          @input="handleInput"
          @paste="handlePaste"
          @click:clear="handleClickClear"
          filled
          @click="handleClickText()"
          @update:modelValue="handleClear(item)"
          rows="1"
          hide-details
          v-model="searchQuery"
          :label="item.label"></v-textarea>
        <div></div>
        <AssetThumb
          v-if="item.value && item.type === 'asset' && !customText"
          :src="assetSmallThumbnail(item.value)"
          @click="quickEditDialog = true"
          :title="
            'Edit ' + (assetCache?.display_name || assetCache?.name || '')
          " />
        <v-progress-linear
          :active="localWaitForLoad"
          :model-value="localLoadNum"
          color="primary"
          absolute
          bottom
          height="4"
          style="border-radius: 5px 5px 0 0"></v-progress-linear>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import EditAssetModal from '../Assets/EditAssetModal.vue';
import AssetThumb from './AssetThumb.vue';
import { state, deluge } from '@/stores/deluge.js';
import { assetSmallThumbnail } from '../../utils/thumbnails.js';
import { $http } from '@/stores/http.js';

const textArea = ref(null);
const loading = ref(false);
const userInteracted = ref(false); // Track if user has clicked or typed

// Generate unique ID for this component instance
const componentId = ref(Math.random().toString(36).substr(2, 9));
console.log('[AssetsDropdown] Component created with ID:', componentId.value);

// Local progress bar state (not global, specific to this component instance)
const localWaitForLoad = ref(false);
const localLoadNum = ref(0);

// Debug: watch local progress state
watch(localWaitForLoad, (val) => {
  console.log('[AssetsDropdown] localWaitForLoad changed to:', val);
});
watch(localLoadNum, (val) => {
  console.log('[AssetsDropdown] localLoadNum changed to:', val);
});

function handleInput(event) {
  console.log('[AssetsDropdown] handleInput - user is typing');
  userInteracted.value = true;
  customText.value = true;
}

function handlePaste(event) {
  console.log('[AssetsDropdown] handlePaste - user pasted');
  userInteracted.value = true;
  customText.value = true;
}

function handleClickClear(event) {
  console.log('[AssetsDropdown] handleClickClear - user clicked clear');
  userInteracted.value = true;
  customText.value = true;
}

function handleClickText() {
  console.log('[AssetsDropdown] handleClickText - user clicked textbox');
  userInteracted.value = true;
  state.textArea = textArea;
  state.item = props.item;
  state.tagQuery = props.tagQuery;
  state.searchQuery = searchQuery.value;
  state.activeDropdownId = componentId.value; // Set active dropdown
  // The overlay (AssetsTemplatesDropdownOverlay) is a singleton that reads
  // state.mode to decide whether to show templates or assets. Multiple
  // dropdowns share that state, so we must reassert it on every click —
  // otherwise the last-mounted dropdown wins, e.g. a templates port shows
  // assets because an asset field below it mounted later.
  state.mode = isTemplateMode ? 'templates' : 'assets';

  if (props.realtime_mode) {
    state.open = false;
    return;
  }

  // Always enable overlay when clicking (but it only shows if hasResults = true)
  // Don't toggle - just enable it and let hasResults control actual visibility
  state.open = true;
  console.log(
    '[AssetsDropdown] Enabled overlay on click (will show only if hasResults)'
  );
}

watch(
  () => state.itemAdded,
  (newValue) => {
    if (state.item == props.item) {
      selectAssets([newValue.item]);
    }
  }
);

const props = defineProps([
  'item',
  'mode',
  'tagQuery',
  'templateUid',
  'realtime_mode',
  'readOnlyMode',
]);

const isTemplateMode = props.mode === 'templates';

async function getMetaData() {
  loading.value = true;
  console.log(
    '[AssetsDropdown] getMetaData started, item.value:',
    props.item.value
  );

  // If item.value is null/empty, clear the search query
  if (!props.item.value) {
    searchQuery.value = '';
    assetCache.value = '';
    customText.value = false;
    loading.value = false;
    console.log(
      '[AssetsDropdown] item.value is null/empty, cleared searchQuery'
    );
    return;
  }

  if (deluge.templates[props.item.value]) {
    searchQuery.value = deluge.templates[props.item.value].display_name;
    assetCache.value = deluge.templates[props.item.value];
    loading.value = false;
    console.log('[AssetsDropdown] Found in deluge.templates');
    return;
  }
  if (deluge.assets[props.item.value]) {
    searchQuery.value = deluge.assets[props.item.value].name;
    assetCache.value = deluge.assets[props.item.value];
    loading.value = false;
    console.log('[AssetsDropdown] Found in deluge.assets');
    return;
  }
  try {
    if (props.item.value) {
      const url = props.mode === 'templates' ? '/get_templates' : '/get_assets';
      const response = await $http(
        'GET',
        url + '?filter_uids=' + props.item.value
      );
      const parsedResponse = JSON.parse(response)[props.item.value];
      if (!parsedResponse) {
        loading.value = false;
        searchQuery.value = props.item.value;
        customText.value = true;
        console.log(
          '[AssetsDropdown] Asset/template not found, set customText=true (should NOT open dropdown)'
        );
        return;
      }
      customText.value = false;
      searchQuery.value = parsedResponse?.display_name
        ? parsedResponse.display_name
        : parsedResponse.name;
      assetCache.value = parsedResponse;
      console.log('[AssetsDropdown] Found via API');
    }
  } catch (error) {
    console.error('[AssetsDropdown] getMetaData error:', error);
  }
  loading.value = false;
}

const emit = defineEmits({
  itemCleared: (item) => item,
  itemAdded: (uidToAdd, item) => ({ uidToAdd, item }),
});

const container = ref(null);
const quickEditDialog = ref(false);

const searchQuery = ref('');
const assetCache = ref('');
const customText = ref(false);

// Local debounce timer for progress animation
let progressTimer = null;
let searchDebounceTimer = null;

// Watch global loading state to complete the animation when API finishes
watch(
  () => state.isSearching,
  (isLoading) => {
    const isActive = state.activeDropdownId === componentId.value;
    console.log(
      '[AssetsDropdown] isSearching changed:',
      isLoading,
      'isActive:',
      isActive,
      'activeDropdownId:',
      state.activeDropdownId,
      'componentId:',
      componentId.value,
      'userInteracted:',
      userInteracted.value
    );

    // Only update local progress if this component is the active one
    if (isActive && userInteracted.value) {
      console.log(
        '[AssetsDropdown] ✅ Loading state changed for THIS component:',
        isLoading
      );

      if (isLoading) {
        // API call started - progress should already be at 80% from typing
        console.log(
          '[AssetsDropdown] API call started (progress already showing)'
        );
      } else {
        // API call finished - complete the animation
        localLoadNum.value = 100;
        console.log(
          '[AssetsDropdown] API call completed, completing progress animation: loadNum=100'
        );

        if (progressTimer) {
          clearTimeout(progressTimer);
        }

        progressTimer = setTimeout(() => {
          localWaitForLoad.value = false;
          console.log(
            '[AssetsDropdown] Hiding progress bar: waitForLoad=false'
          );
          setTimeout(() => {
            localLoadNum.value = 0;
            console.log('[AssetsDropdown] Reset progress: loadNum=0');
          }, 100);
        }, 100);
      }
    } else {
      console.log(
        '[AssetsDropdown] ❌ NOT updating progress (not active or no user interaction)'
      );
    }
  }
);

watch(searchQuery, (newVal, oldVal) => {
  console.log('[AssetsDropdown] searchQuery changed:', {
    newVal,
    oldVal,
    loading: loading.value,
    customText: customText.value,
    userInteracted: userInteracted.value,
    isTemplateMode,
  });

  // Always update the overlay's search query for filtering
  state.searchQuery = searchQuery.value;

  // If searchQuery changed and we're not loading initial data, treat it as custom text
  // (user is typing or modifying the field)
  if (!loading.value && newVal !== oldVal) {
    // Only set customText if user is actively interacting (not when programmatically setting after selection)
    // BUT: For templates, never allow custom text (only allow selection from dropdown or empty)
    if (userInteracted.value) {
      if (!isTemplateMode) {
        // Asset mode: allow custom text
        customText.value = true;
        console.log(
          '[AssetsDropdown] Set customText to true (user typing in asset mode)'
        );

        // Trigger local "fake" progress animation (mimicking pseudoLoader behavior)
        if (searchDebounceTimer) {
          clearTimeout(searchDebounceTimer);
        }

        // Show progress immediately at 80%
        localWaitForLoad.value = true;
        localLoadNum.value = 80;
        console.log(
          '[AssetsDropdown] Started immediate progress animation (fake loading while typing)'
        );

        // The actual API call will be debounced, and when it completes,
        // state.isSearching will change and complete the animation
      } else {
        // Template mode: typing is only for filtering, not setting custom value
        console.log(
          '[AssetsDropdown] Template mode - typing only for filtering, not setting custom text'
        );

        // Still show progress for filtering
        if (searchDebounceTimer) {
          clearTimeout(searchDebounceTimer);
        }
        localWaitForLoad.value = true;
        localLoadNum.value = 80;
      }
    }
  }

  // Only update item.value with search text if user is actively typing (not after selection)
  // AND only in asset mode (templates must select from dropdown)
  if (customText.value && userInteracted.value && !isTemplateMode) {
    props.item.value = searchQuery.value;
    console.log(
      '[AssetsDropdown] Set item.value to search text:',
      searchQuery.value
    );
  }

  // When user interacts, enable the overlay (it will decide when to show based on hasResults)
  if (userInteracted.value && !props.realtime_mode && !props.readOnlyMode) {
    state.textArea = textArea;
    state.item = props.item;
    state.tagQuery = props.tagQuery;
    state.activeDropdownId = componentId.value; // Track which dropdown is active
    state.open = true; // Enable overlay (but it only shows if hasResults = true)
    console.log(
      '[AssetsDropdown] Enabled overlay (will show only if hasResults), set activeDropdownId:',
      componentId.value
    );
  }
});

function handleClear() {
  console.log(
    '[AssetsDropdown] handleClear called, isTemplateMode:',
    isTemplateMode
  );
  customText.value = false;
  props.item.value = '';
  searchQuery.value = '';
  state.searchQuery = '';
  // Don't auto-open dropdown when clearing - let user click or type to open
  emit('itemCleared', state.item);
}
function selectAssets(assets) {
  console.log(
    '[AssetsDropdown] selectAssets called with asset:',
    assets[0].uid,
    assets[0].display_name || assets[0].name
  );
  console.log('[AssetsDropdown] Closing dropdown and resetting flags');

  customText.value = false;
  userInteracted.value = false; // Reset so that setting searchQuery doesn't reopen dropdown

  state.open = false;

  // Set the UUID first (this is what the backend needs)
  props.item.value = assets[0].uid;
  console.log('[AssetsDropdown] Set item.value to UUID:', assets[0].uid);

  // Then set searchQuery to display name (for user to see)
  searchQuery.value = assets[0].display_name || assets[0].name;
  state.searchQuery = searchQuery.value;
  assetCache.value = assets[0];

  emit('itemAdded', assets[0].uid, props.item);
}

onMounted(() => {
  state.mode = props.mode === 'templates' ? 'templates' : 'assets';

  getMetaData();
});

// Watch for changes to item.value (e.g., when navigating between nested levels)
// When the parent changes which item we're looking at, we need to refresh
watch(
  () => props.item,
  (newItem, oldItem) => {
    // Check if the item reference itself changed OR if the value property changed
    if (newItem !== oldItem || newItem?.value !== oldItem?.value) {
      console.log(
        '[AssetsDropdown] item prop changed, refreshing metadata. Old value:',
        oldItem?.value,
        'New value:',
        newItem?.value
      );
      // Reset state for the new item
      customText.value = false;
      userInteracted.value = false;
      assetCache.value = '';
      // Fetch metadata for the new item
      getMetaData();
    }
  },
  { deep: true } // Watch for changes in nested properties like item.value
);

onBeforeUnmount(() => {
  // Clean up timers
  if (progressTimer) {
    clearTimeout(progressTimer);
  }
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }

  // Clean up state if this textArea was the one the overlay was attached to
  if (state.textArea === textArea) {
    state.open = false;
    state.textArea = null;
    state.item = null;
    state.tagQuery = null;
  }
});
</script>
<!-- styles moved into AssetThumb.vue -->
